import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  CheckCircle, 
  Clock, 
  Share2, 
  ShoppingCart, 
  Package, 
  AlertCircle,
  Loader2,
  Copy
} from 'lucide-react';
import { useSnapshot } from '@/hooks/useSnapshot';
import { SnapshotData } from '@/services/snapshotService';

export default function SnapshotView() {
  const { snapshotId } = useParams<{ snapshotId: string }>();
  const navigate = useNavigate();
  const { loadSnapshot, restoreState, getShareMessage } = useSnapshot();
  const [snapshot, setSnapshot] = useState<SnapshotData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    const loadSnapshotData = async () => {
      if (!snapshotId) {
        setError('ID de snapshot no válido');
        setLoading(false);
        return;
      }

      try {
        const data = await loadSnapshot(snapshotId);
        if (data) {
          setSnapshot(data);
        } else {
          setError('Snapshot no encontrado o expirado');
        }
      } catch (err) {
        setError('Error al cargar el snapshot');
        console.error('Error loading snapshot:', err);
      } finally {
        setLoading(false);
      }
    };

    loadSnapshotData();
  }, [snapshotId, loadSnapshot]);

  const handleRestoreAndContinue = () => {
    if (snapshot) {
      restoreState();
      // Navigate to the stage where the snapshot was created
      navigate(`/eleccion/${snapshot.metadata.stage}`);
    }
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy URL:', error);
    }
  };

  const handleShare = async () => {
    if (!snapshot) return;

    const message = getShareMessage();
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Mi selección CPAP - buno',
          text: message,
          url: window.location.href
        });
      } catch (error) {
        console.error('Error sharing:', error);
        handleCopyUrl();
      }
    } else {
      handleCopyUrl();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Cargando selección...</p>
        </div>
      </div>
    );
  }

  if (error || !snapshot) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-6 text-center space-y-4">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto" />
            <h2 className="text-xl font-semibold">Snapshot no disponible</h2>
            <p className="text-muted-foreground">
              {error || 'Este enlace puede haber expirado o no ser válido.'}
            </p>
            <Button onClick={() => navigate('/')}>
              Ir al inicio
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { userData, metadata } = snapshot;
  const isExpired = snapshot.status === 'expired' || new Date() > snapshot.expiresAt;

  return (
    <div className="min-h-screen bg-background">
      <div className="container px-4 py-8 max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary mb-2">
            Selección CPAP Compartida
          </h1>
          <p className="text-muted-foreground">
            Configuración guardada el {snapshot.createdAt.toLocaleDateString('es-ES', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </p>
          
          {/* Status badges */}
          <div className="flex justify-center gap-2 mt-4">
            <Badge variant={isExpired ? "destructive" : "default"}>
              {isExpired ? "Expirado" : "Activo"}
            </Badge>
            <Badge variant="outline">
              Etapa: {metadata.stage}
            </Badge>
          </div>
        </div>

        <div className="grid gap-6">
          {/* Equipment Selection */}
          {userData.equipoSeleccionado && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Equipo CPAP Seleccionado
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-start gap-4">
                  <div className="w-20 h-20 bg-white rounded-lg overflow-hidden border border-border/10">
                    <img 
                      src={userData.equipoSeleccionado.imagen} 
                      alt={userData.equipoSeleccionado.modelo}
                      className="w-full h-full object-contain" 
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-primary">{userData.equipoSeleccionado.modelo}</h3>
                    <p className="text-muted-foreground">{userData.equipoSeleccionado.marca}</p>
                    <div className="mt-3 space-y-1">
                      {userData.equipoSeleccionado.caracteristicas.map((feature, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <CheckCircle className="h-3 w-3" />
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Mask Selection */}
          {userData.mascarillaSeleccionada && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5" />
                  Mascarilla Seleccionada
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-start gap-4">
                  <div className="w-20 h-20 bg-white rounded-lg overflow-hidden border border-border/10">
                    <img 
                      src={userData.mascarillaSeleccionada.imagen} 
                      alt={userData.mascarillaSeleccionada.modelo}
                      className="w-full h-full object-contain" 
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-primary">{userData.mascarillaSeleccionada.modelo}</h3>
                    <p className="text-muted-foreground">
                      {userData.mascarillaSeleccionada.marca} • {userData.mascarillaSeleccionada.tipo}
                    </p>
                    {userData.maskSizing && (
                      <div className="mt-2">
                        <Badge variant="secondary">
                          Talla: {userData.maskSizing.maskSize}
                        </Badge>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Buno+ Program */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                Programa buno+
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">
                    {userData.bunoIncluido ? 'Incluido en la selección' : 'No incluido'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Acompañamiento integral 24/7
                  </p>
                </div>
                {userData.bunoIncluido && (
                  <p className="text-xl font-bold text-accent">$2,500</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Bundle Info */}
          {userData.bundleInfo && (
            <Card>
              <CardHeader>
                <CardTitle>Información del Paquete</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <h4 className="font-semibold">{userData.bundleInfo.name}</h4>
                  <p className="text-sm text-muted-foreground" 
                     dangerouslySetInnerHTML={{ __html: userData.bundleInfo.description }} />
                  <p className="text-xl font-bold text-primary">
                    ${userData.bundleInfo.price.toLocaleString()}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <Card>
            <CardContent className="p-6">
              <div className="space-y-4">
                {!isExpired && (
                  <Button 
                    className="w-full" 
                    size="lg"
                    onClick={handleRestoreAndContinue}
                  >
                    <ShoppingCart className="h-5 w-5 mr-2" />
                    Continuar con esta selección
                  </Button>
                )}
                
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={handleShare}
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    Compartir
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={handleCopyUrl}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    {copySuccess ? 'Copiado!' : 'Copiar URL'}
                  </Button>
                </div>

                <Separator />

                
              </div>
            </CardContent>
          </Card>

          {/* Expiration notice */}
          {isExpired && (
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-orange-600" />
                  <div>
                    <p className="text-sm font-medium text-orange-800">
                      Esta selección ha expirado
                    </p>
                    <p className="text-xs text-orange-600">
                      Expiró el {snapshot.expiresAt.toLocaleDateString('es-ES')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}